import {
  Appear,
  Divider,
  Flex,
  hooks,
  Icon,
  Link,
  Skeleton,
  SkeletonBox,
  Text,
} from '@bitrise/bitkit';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import dataAtIds from '../../../dataAtIds.json';
import useCrash from '../useCrash';
import useCrashEventList from '../useCrashEventList';
import { FeatureContext } from '../../Features/useFeatures';
import { trackEvent } from '../../../utils/tracking';
import Page from '../../Page/Page';
import PageBody from '../../Page/PageBody';
import PageBreadcrumb from '../../Page/PageBreadcrumb';
import PageHeader from '../../Page/PageHeader';
import PageTitle from '../../Page/PageTitle';
import { URLStateContext } from '../../URLState/URLState';
import CrashAttributes from './CrashAttributes';
import CrashDistribution from './CrashDistribution';
import CrashEvent from './CrashEvent/CrashEvent';
import CrashStatusToggle from './CrashStatusToggle';

const { useMediaQuery } = hooks;

interface Params {
  appId: string;
  crashId: string;
  eventId?: string;
}

const CrashPage: FunctionComponent = () => {
  const { appId, crashId, eventId } = useParams<Params>();
  const { hasFeature } = useContext(FeatureContext);
  const matchQuery = useMediaQuery(['700px', '1056px']);
  const { replace } = useContext(URLStateContext);
  const { data: crash, refetch, isError, isFetching } = useCrash(crashId);
  const { firstCrashEventId } = useCrashEventList(crashId);

  /**
   * Use a side-effect to:
   *  - Forward the user onto the first available crash event URL, if landing directly on the /event route
   */
  useEffect(() => {
    if (!eventId && firstCrashEventId !== null) {
      replace(`/${appId}/crashes/${crashId}/event/${firstCrashEventId}`);
    }
  }, [appId, crashId, eventId, firstCrashEventId, replace]);

  /**
   * Use a side-effect to:
   *  - Track visits to this page (i.e. whenever this component is mounted)
   */
  useEffect(() => {
    trackEvent('IssueDetails.Visit', {});
  }, []);

  return (
    <Page data-trace-at-id={dataAtIds.CrashPage}>
      {isError ? (
        <Flex
          alignChildrenVertical="middle"
          direction="vertical"
          grow
          data-trace-at-id={dataAtIds.CrashPageDetailsErrorRetry}
        >
          <Appear animation="Fade" textColor="red-3">
            <Flex alignChildren="middle" direction="vertical" height="100%">
              <Flex>
                <Flex alignChildren="middle" direction="vertical">
                  <Icon margin="x1" name="BitbotFailed" size="3rem" />
                </Flex>
                <Text align="middle" ellipsis>
                  Uh oh, there&apos;s been a problem.
                </Text>
                <Text align="middle">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link clickable onClick={refetch} underline>
                    Click here to retry
                  </Link>
                  .
                </Text>
              </Flex>
            </Flex>
          </Appear>
        </Flex>
      ) : (
        <>
          <PageHeader>
            <Flex
              alignChildrenVertical="end"
              direction={matchQuery('1056px') ? 'horizontal' : 'vertical'}
              gap="x5"
            >
              <Flex grow initial="none">
                <PageBreadcrumb to={`/${appId}/crashes`}>All Crashes</PageBreadcrumb>
                <PageTitle>
                  <Skeleton active={isFetching}>
                    {!isFetching && !!crash ? crash.title : <SkeletonBox height="2rem" />}
                  </Skeleton>
                </PageTitle>
              </Flex>

              {!!crash && (
                <Flex>
                  <CrashStatusToggle defaultStatus={crash.status} />
                </Flex>
              )}
            </Flex>

            <Divider color="gray-2" margin="x6" width="1px" />

            <Skeleton active={isFetching} margin="x6">
              <CrashAttributes crash={crash} isFetching={isFetching} />
            </Skeleton>

            {hasFeature('enable-crashes-distribution') && <CrashDistribution />}
          </PageHeader>

          <PageBody>
            <CrashEvent />
          </PageBody>
        </>
      )}
    </Page>
  );
};

export default CrashPage;
